import { useRef, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { PagedVideoItemDto } from "@repo/api/video";
import { Link, useNavigate, useParams } from "react-router";
import { formatAudioDuration, formatCreatedAt } from "@/lib/utils";
import {
  IconCopy,
  IconDotsVertical,
  IconFolderSymlink,
  IconPencil,
  IconTrash,
  IconVideo,
} from "@tabler/icons-react";
import Image from "@/lib/components/Image";
import { paths } from "@/App";
import { getCompositionDurationMs } from "@repo/video-engine-i2v/utils/timing";
import ImageBadge from "@/lib/components/ImageBadge";
import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/lib/components/Popover";
import {
  useDeleteVideoMutation,
  useDuplicateVideoMutation,
  useRenameVideoMutation,
} from "@/lib/queries/video-queries";
import { useMoveVideoToFolderMutation } from "@/lib/queries/folder-queries";
import NameDialog from "./NameDialog";
import ConfirmDeleteDialog from "@/lib/components/ConfirmDeleteDialog";
import FolderPicker from "./FolderPicker";
import { VideosDragData } from "./videos-dnd";

type Props = {
  video: PagedVideoItemDto;
  /** Set when the video is rendered inside a folder view. */
  folderExternalId?: string;
};

export default function VideoListItem({ video, folderExternalId }: Props) {
  const { teamExternalId } = useParams();
  const navigate = useNavigate();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const { listeners, setNodeRef, isDragging, active } = useDraggable({
    id: `video:${video.externalId}`,
    data: { type: "video", video } satisfies VideosDragData,
  });

  const renameVideoMutation = useRenameVideoMutation({
    teamExternalId: teamExternalId!,
    onSuccess: () => setRenameOpen(false),
  });
  const deleteVideoMutation = useDeleteVideoMutation({
    teamExternalId: teamExternalId!,
    onSuccess: () => setDeleteOpen(false),
  });
  const duplicateVideoMutation = useDuplicateVideoMutation({
    teamExternalId: teamExternalId!,
    onSuccess: (newVideo) =>
      navigate(paths.videoEdit(teamExternalId!, newVideo.externalId)),
  });
  const moveVideoMutation = useMoveVideoToFolderMutation({
    teamExternalId: teamExternalId!,
    onSuccess: () => setMoveOpen(false),
  });

  const durationMs = getCompositionDurationMs(
    video.compositionJson,
    video.animatedImages,
  );

  const isActive = menuOpen || moveOpen || renameOpen || deleteOpen;

  return (
    <Popover open={moveOpen} onOpenChange={setMoveOpen}>
      <div
        ref={setNodeRef}
        {...listeners}
        className={`group relative transition-transform duration-200 ${
          // While any drag is active, disable hover and click interactions so
          // dropping on a video card cannot trigger its hover state or open it.
          active ? "pointer-events-none" : "hover:scale-[1.02]"
        } ${isActive ? "scale-[1.02]" : ""} ${isDragging ? "opacity-50" : ""}`}
      >
        <Link
          to={paths.videoEdit(teamExternalId!, video.externalId)}
          className="flex flex-col rounded-t-xl"
        >
          <div className="relative aspect-video self-stretch overflow-hidden rounded-xl">
            {video.thumbnailUrl ? (
              <Image
                src={video.thumbnailUrl}
                alt="Video thumbnail"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-100">
                <IconVideo size={48} className="text-text-secondary" />
              </div>
            )}
            {durationMs > 0 && (
              <ImageBadge className="right-3 bottom-2.5 rounded">
                {formatAudioDuration(durationMs)}
              </ImageBadge>
            )}
          </div>
          <div className="h-2"></div>
          <p className="line-clamp-1 font-semibold">
            {video.name || "Untitled video"}
          </p>
          <p className="line-clamp-1 text-sm text-text-secondary">
            {formatCreatedAt(video.updatedAt, "Edited")}
          </p>
        </Link>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            className="absolute top-2 right-2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="iconXs"
                  className={`opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 ${
                    isActive ? "opacity-100" : ""
                  }`}
                  aria-label="Video options"
                >
                  <IconDotsVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                  <IconPencil size={16} />
                  Rename video
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMoveOpen(true)}>
                  <IconFolderSymlink size={16} />
                  Move to folder
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={duplicateVideoMutation.isPending}
                  onSelect={() =>
                    duplicateVideoMutation.mutate(video.externalId)
                  }
                >
                  <IconCopy size={16} />
                  Duplicate video
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-error"
                  onSelect={() => setDeleteOpen(true)}
                >
                  <IconTrash size={16} />
                  Delete video
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="end"
          className="w-72 p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (anchorRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <FolderPicker
            teamExternalId={teamExternalId!}
            currentLocationExternalId={folderExternalId ?? null}
            allowRoot={!!folderExternalId}
            isMoving={moveVideoMutation.isPending}
            onSelect={async (targetFolderExternalId) => {
              try {
                await moveVideoMutation.mutateAsync({
                  videoExternalId: video.externalId,
                  folderExternalId: targetFolderExternalId,
                });
                navigate(
                  targetFolderExternalId
                    ? paths.videosFolder(
                        teamExternalId!,
                        targetFolderExternalId,
                      )
                    : paths.videos(teamExternalId!),
                );
              } catch {
                // Error is surfaced by the mutation's onError toast.
              }
            }}
          />
        </PopoverContent>
        <NameDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          title="Rename video"
          description={`Rename ${video.name ? `"${video.name}"` : "Untitled video"}.`}
          initialName={video.name}
          placeholder="Video name"
          submitLabel="Rename"
          isSubmitting={renameVideoMutation.isPending}
          onSubmit={(name) =>
            renameVideoMutation.mutate({
              videoExternalId: video.externalId,
              data: { name },
            })
          }
        />
        <ConfirmDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete video"
          description={`"${video.name || "Untitled video"}" will be permanently deleted.`}
          confirmLabel="Delete"
          isDeleting={deleteVideoMutation.isPending}
          onConfirm={() => deleteVideoMutation.mutate(video.externalId)}
        />
      </div>
    </Popover>
  );
}
